import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { User } from '../types/alarm';
import PasswordChangeModal from './PasswordChangeModal';
import UserAccountAvatarCropModal from './UserAccountAvatarCropModal';
import { userDataService } from '../services/userDataService';
import { clearUserFromStorage, deleteFirebaseUser } from '../utils/userStorage';
import { auth, storage } from '../firebase/config';
import { IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { buildUserStoragePath } from '../utils/userStoragePaths';
import {
  getAvatarInitialsFromEmail,
  getUiAvatarPlaceholderUrl,
  isUiAvatarsPlaceholderUrl
} from '../utils/uiAvatarPlaceholder';

interface ProfileEditModalProps {
  isOpen: boolean;
  user: User;
  onSave: (updatedUser: User) => void;
  onCancel: () => void;
  /** 회원탈퇴 성공 시에만 호출 (로그아웃 버튼은 없음) */
  onLogout?: () => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 80px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  margin: auto;
  pointer-events: auto;
  border: var(--modal-border);
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
  font-size: 1.3rem;
  text-align: center;
`;

const AccountInfo = styled.div`
  margin-bottom: 12px;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  line-height: 1.2;
`;

const UserTypeInfo = styled.div`
  margin-bottom: 12px;
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  font-weight: normal;
  line-height: 1.2;
`;

const LicensePeriodInfo = styled.div`
  margin-bottom: 20px;
  text-align: center;
  color: #999;
  font-size: 0.95rem;
  font-weight: normal;
  line-height: 1.2;
`;

const ProfileAvatarBlock = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
`;

const ProfileAvatarRing = styled.div<{ $dimmed?: boolean }>`
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  opacity: ${(p) => (p.$dimmed ? 0.65 : 1)};
  pointer-events: ${(p) => (p.$dimmed ? 'none' : 'auto')};
`;

const ProfileAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ProfileAvatarHover = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.18s ease;

  ${ProfileAvatarRing}:hover & {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
    background: rgba(0, 0, 0, 0.38);
  }
`;

const ProfileAvatarEditBtn = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 20px;
`;

const Button = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border);
    
    &:hover {
      background: var(--color-panel);
      border-color: var(--color-text-muted);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PasswordChangeButton = styled.button`
  background: var(--color-panel);
  color: var(--color-text-gray);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  
  &:hover {
    background: var(--color-border);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 16px 0;
`;

const DeleteAccountTextButton = styled.button`
  display: block;
  width: fit-content;
  max-width: 100%;
  margin: 8px auto 0;
  background: none;
  border: none;
  color: var(--color-text-gray);
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    color: var(--color-accent-text);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10002;
`;

const ConfirmModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px;
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 10003;
  max-height: 90vh;
  overflow-y: auto;
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const ConfirmMessage = styled.p`
  margin: 0 0 12px 0;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  text-align: left;
  
  strong {
    font-weight: 600;
  }
`;

const ConfirmWarning = styled.div`
  margin: 16px 0;
  padding: 12px;
  background: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: left;
`;

const ConfirmWarningLicense = styled(ConfirmWarning)`
  font-weight: 700;
  color: #ffffff;
  [data-theme='light'] & {
    color: var(--color-accent-strong);
  }
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--color-surface);
  color: var(--color-text-primary);
  margin: 12px 0;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ConfirmButton = styled.button<{ destructive?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.destructive ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border);
    
    &:hover:not(:disabled) {
      background: var(--color-panel);
      border-color: var(--color-text-muted);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ErrorText = styled.div`
  color: var(--color-accent-strong);
  font-size: 0.9rem;
  margin: 8px 0;
`;

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  user,
  onSave,
  onCancel,
  onLogout
}) => {
  const [name, setName] = useState(user.name);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showAvatarCropModal, setShowAvatarCropModal] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [isGoogleAuth, setIsGoogleAuth] = useState(false);
  const [hasPasswordProvider, setHasPasswordProvider] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const cu = auth.currentUser;
    const providers = cu?.providerData ?? [];
    setIsGoogleAuth(providers.some((p) => p.providerId === 'google.com'));
    setHasPasswordProvider(providers.some((p) => p.providerId === 'password'));
  }, [isOpen, user.id]);

  const profileDisplaySrc = useMemo(() => {
    const raw = user.picture?.trim();
    if (raw && !isUiAvatarsPlaceholderUrl(raw)) return raw;
    return getUiAvatarPlaceholderUrl(getAvatarInitialsFromEmail(user.email), { size: 128 });
  }, [user.picture, user.email]);

  // 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // 별칭이 설정되어 있으면 별칭 사용, 없으면 빈 문자열
      setName(user.name || '');
    }
  }, [isOpen, user]);

  const handleSave = () => {
    // 별칭이 비어있으면 Google의 실제 이름 사용
    const displayName = name.trim() || user.name;
    
    onSave({
      ...user,
      name: displayName
    });
    
    // 저장 후 모달 닫기
    onCancel();
  };

  const getUserTypeText = (userType: string) => {
    switch (userType) {
      case 'pro':
        return '프로';
      case 'basic':
        return '베이직';
      case 'free':
        return '프리';
      case 'admin':
        return '관리자';
      default:
        return '프리';
    }
  };

  const getLicensePeriodText = () => {
    if (user.userType === 'free') {
      return '무제한';
    }
    
    if (user.license) {
      const purchasedAt = user.license.purchasedAt instanceof Date 
        ? user.license.purchasedAt 
        : new Date((user.license.purchasedAt as any)?.toDate?.() ?? user.license.purchasedAt);
      
      const expiresAt = user.license.expiresAt instanceof Date 
        ? user.license.expiresAt 
        : new Date((user.license.expiresAt as any)?.toDate?.() ?? user.license.expiresAt);
      
      if (isNaN(purchasedAt.getTime()) || isNaN(expiresAt.getTime())) {
        return '정보 없음';
      }
      
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}.${month}.${day}`;
      };
      
      return `${formatDate(purchasedAt)} ~ ${formatDate(expiresAt)}`;
    }
    
    return '정보 없음';
  };

  /** 이용권 구매 후 7일 이내 = 환불 가능, 이용권 기간 남음 = 탈퇴 시 소멸 경고 */
  const REFUND_ELIGIBLE_DAYS = 7;
  const getLicenseStatus = (): { refundEligible: boolean; hasRemainingPeriod: boolean } => {
    if (!user.license) return { refundEligible: false, hasRemainingPeriod: false };
    const purchasedAt = user.license.purchasedAt instanceof Date
      ? user.license.purchasedAt
      : new Date((user.license.purchasedAt as any)?.toDate?.() ?? user.license.purchasedAt);
    const expiresAt = user.license.expiresAt instanceof Date
      ? user.license.expiresAt
      : new Date((user.license.expiresAt as any)?.toDate?.() ?? user.license.expiresAt);
    if (isNaN(purchasedAt.getTime()) || isNaN(expiresAt.getTime())) {
      return { refundEligible: false, hasRemainingPeriod: false };
    }
    const now = Date.now();
    const sevenDaysMs = REFUND_ELIGIBLE_DAYS * 24 * 60 * 60 * 1000;
    const refundEligible = now - purchasedAt.getTime() < sevenDaysMs;
    const hasRemainingPeriod = expiresAt.getTime() > now;
    return { refundEligible, hasRemainingPeriod };
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeleting) return;
    const isConfirmed = confirmText.trim() === user.email;
    if (!isConfirmed) {
      setDeleteError('계정 이메일을 입력해주세요.');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    const userId = user.id;
    try {
      // 1. 반드시 Auth 삭제만 먼저 시도. 실패 시(예: 최근 로그인 필요) Firestore/DB는 절대 삭제하지 않음.
      await deleteFirebaseUser();

      // 2. Auth 삭제 성공 후에만 Firestore 사용자 데이터 삭제 (실패 시 위에서 throw 되어 여기 도달 안 함)
      await userDataService.deleteUserAccount(userId);

      // 로컬 스토리지 정리
      clearUserFromStorage();

      onLogout?.();
    } catch (error: any) {
      console.error('회원탈퇴 실패:', error);
      setDeleteError(error?.message || '회원탈퇴에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsDeleting(false);
    }
  };

  const canDelete = confirmText.trim() === user.email;

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay isOpen={isOpen} onClick={onCancel} />
      <ModalWrapper>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>프로필 편집</Title>
        <AccountInfo>계정: {user.email}</AccountInfo>
        
        <UserTypeInfo>유형: {getUserTypeText(user.userType)}</UserTypeInfo>
        <LicensePeriodInfo>이용기간: {getLicensePeriodText()}</LicensePeriodInfo>

        <ProfileAvatarBlock>
          <ProfileAvatarRing $dimmed={avatarUploading}>
            <ProfileAvatarImg src={profileDisplaySrc} alt="" referrerPolicy="no-referrer" />
            {!isGoogleAuth && (
              <ProfileAvatarHover>
                <ProfileAvatarEditBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAvatarError('');
                    setShowAvatarCropModal(true);
                  }}
                  aria-label="프로필 이미지 수정"
                >
                  수정
                </ProfileAvatarEditBtn>
              </ProfileAvatarHover>
            )}
          </ProfileAvatarRing>
        </ProfileAvatarBlock>
        {avatarError && <ErrorText style={{ textAlign: 'center', marginBottom: 8 }}>{avatarError}</ErrorText>}

        <FormGroup>
          <Label>이름</Label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="이름을 입력하세요 (선택사항)"
            autoFocus
          />
        </FormGroup>

        <ButtonGroup>
          <Button data-variant="modal-secondary" onClick={onCancel}>닫기</Button>
          <Button primary onClick={handleSave}>저장</Button>
        </ButtonGroup>
        <Divider />
        {hasPasswordProvider && (
          <PasswordChangeButton onClick={() => setShowPasswordChange(true)}>
            비밀번호 변경
          </PasswordChangeButton>
        )}
        <DeleteAccountTextButton onClick={() => {
          const { refundEligible } = getLicenseStatus();
          if (refundEligible) {
            alert('이용권 구매 후 7일 이내에는 환불이 가능합니다. 먼저 환불을 진행한 후 회원탈퇴를 진행해 주세요.');
            return;
          }
          setShowDeleteConfirm(true);
          setConfirmText('');
          setDeleteError('');
        }}>
          회원탈퇴
        </DeleteAccountTextButton>
        <PasswordChangeModal isOpen={showPasswordChange} onClose={() => setShowPasswordChange(false)} userEmail={user.email} />
        </ModalContent>
      </ModalWrapper>

      {!isGoogleAuth && (
        <UserAccountAvatarCropModal
          isOpen={showAvatarCropModal}
          onClose={() => setShowAvatarCropModal(false)}
          onSave={async (blob) => {
            setAvatarUploading(true);
            setAvatarError('');
            try {
              const objectPath = buildUserStoragePath(
                user.id,
                'account',
                `profile_${Date.now()}.${IMAGE_WEBP_EXT}`
              );
              const contentType = IMAGE_WEBP_MIME;
              const storageRef = ref(storage, objectPath);
              await uploadBytes(storageRef, blob, { contentType });
              let downloadURL = await getDownloadURL(storageRef);
              downloadURL += downloadURL.includes('?') ? `&cb=${Date.now()}` : `?cb=${Date.now()}`;
              const displayName = name.trim() || user.name;
              const merged: User = { ...user, picture: downloadURL, name: displayName };
              onSave(merged);
            } catch (err) {
              console.error(err);
              setAvatarError('프로필 이미지를 저장하지 못했습니다. 다시 시도해 주세요.');
              throw err;
            } finally {
              setAvatarUploading(false);
            }
          }}
        />
      )}

      {showDeleteConfirm && (
        <>
          <ConfirmModalOverlay onClick={() => !isDeleting && setShowDeleteConfirm(false)} />
          <ConfirmModalContent>
            <ConfirmTitle>회원탈퇴</ConfirmTitle>
            <ConfirmMessage>
              회원탈퇴 시 모든 데이터가 영구 삭제되며 복구할 수 없습니다. 계속하시겠습니까?
            </ConfirmMessage>
            {getLicenseStatus().hasRemainingPeriod && (
              <ConfirmWarningLicense>
                사용 기간이 남은 이용권이 있습니다. 지금 회원 탈퇴를 하시면 이용권은 소멸되며 복구할 수 없습니다.
              </ConfirmWarningLicense>
            )}
            <ConfirmWarning>
              데이터베이스의 사용자 문서(클래스, 멤버, 알람, 과제, 포인트, 기록 등)의 모든 데이터가 즉시 삭제됩니다.<br />
              데이터베이스의 사용자 정보도 삭제되어 다시 로그인 할 수 없습니다. (로그인을 위해 재가입 필요)
            </ConfirmWarning>
            <ConfirmMessage>
              인증을 위해 계정 이메일을 입력하세요.
            </ConfirmMessage>
            <ConfirmInput
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="example@edukodi.com"
              disabled={isDeleting}
            />
            {deleteError && <ErrorText>{deleteError}</ErrorText>}
            <ConfirmButtonRow>
              <ConfirmButton onClick={() => !isDeleting && setShowDeleteConfirm(false)} disabled={isDeleting}>
                취소
              </ConfirmButton>
              <ConfirmButton
                destructive
                onClick={handleDeleteAccount}
                disabled={!canDelete || isDeleting}
              >
                {isDeleting ? '회원탈퇴 중...' : '회원탈퇴'}
              </ConfirmButton>
            </ConfirmButtonRow>
          </ConfirmModalContent>
        </>
      )}
    </>
  );
};

export default ProfileEditModal; 